import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import ToastNotification from '../../components/ToastNotification';
import styles from './BranchWeeklyProfits.module.css';

function getMondayOfWeek(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function getSundayOfWeek(mondayStr) {
  const d = new Date(mondayStr + 'T00:00:00');
  d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
}

function formatWeekLabel(mondayStr) {
  const start = new Date(mondayStr + 'T00:00:00');
  const end   = new Date(mondayStr + 'T00:00:00');
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

function formatTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-PH', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function php(n) {
  return `₱${(n || 0).toLocaleString()}`;
}

const today = new Date().toISOString().slice(0, 10);

export default function BranchWeeklyProfits() {
  const { user } = useAuth();
  const [records,    setRecords]    = useState([]);
  const [orders,     setOrders]     = useState([]);
  const [showModal,  setShowModal]  = useState(false);
  const [weekDate,   setWeekDate]   = useState(getMondayOfWeek(today));
  const [revenue,    setRevenue]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors,     setErrors]     = useState({});
  const [toast,      setToast]      = useState(null);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, 'weeklyProfits'), orderBy('weekStart', 'desc'));
    return onSnapshot(q, snap => {
      setRecords(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.branchId === user.uid)
      );
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, 'orders'), where('branchId', '==', user.uid));
    return onSnapshot(q, snap => setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user?.uid]);

  const monday = getMondayOfWeek(weekDate);
  const sunday = getSundayOfWeek(monday);

  const supplyExpense = useMemo(() => {
    const start = new Date(monday + 'T00:00:00').getTime();
    const end   = new Date(sunday + 'T23:59:59').getTime();
    return orders
      .filter(o => {
        const ms = o.createdAt?.toMillis?.() ?? 0;
        return ms >= start && ms <= end;
      })
      .reduce((sum, o) => sum + (o.total || 0), 0);
  }, [orders, monday, sunday]);

  const revenueNum = parseFloat(revenue) || 0;
  const netProfit  = revenueNum - supplyExpense;

  function openModal() {
    setWeekDate(getMondayOfWeek(today));
    setRevenue('');
    setErrors({});
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setErrors({});
  }

  async function handleSubmit() {
    const errs = {};
    if (!revenue || isNaN(parseFloat(revenue))) errs.revenue = 'Enter a valid amount';
    else if (parseFloat(revenue) < 0)           errs.revenue = 'Amount must be ≥ 0';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'weeklyProfits'), {
        branchId:      user.uid,
        branchName:    user.name || '',
        weekStart:     monday,
        weekEnd:       sunday,
        weekLabel:     formatWeekLabel(monday),
        weeklyRevenue: revenueNum,
        supplyExpense,
        netProfit:     revenueNum - supplyExpense,
        createdAt:     serverTimestamp(),
        updatedAt:     serverTimestamp(),
      });
      closeModal();
      setToast({ message: 'Weekly profit logged successfully.', type: 'success' });
    } catch (e) {
      console.error(e);
      setToast({ message: 'Failed to save. Please try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const totals = useMemo(() => records.reduce(
    (acc, r) => ({
      revenue: acc.revenue + (r.weeklyRevenue || 0),
      expense: acc.expense + (r.supplyExpense  || 0),
      net:     acc.net     + (r.netProfit      || 0),
    }),
    { revenue: 0, expense: 0, net: 0 },
  ), [records]);

  return (
    <div className={styles.page}>
      <PageHeader title="Weekly Profits" description="Log and track your branch's weekly profit records" />

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{records.length}</span>
          <span className={styles.statLabel}>Total Entries</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{php(totals.revenue)}</span>
          <span className={styles.statLabel}>Total Revenue</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{php(totals.expense)}</span>
          <span className={styles.statLabel}>Supply Expenses</span>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statValue} ${totals.net >= 0 ? styles.positive : styles.negative}`}>
            {php(totals.net)}
          </span>
          <span className={styles.statLabel}>Net Profit</span>
        </div>
      </div>

      <div className={styles.tableHeader}>
        <span className={styles.tableTitle}>Profit Records</span>
        <button className={styles.logBtn} onClick={openModal}>
          <i className="ti ti-plus" /> Log This Week&apos;s Profit
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Week</th>
              <th>Weekly Revenue</th>
              <th>Supply Expense</th>
              <th>Net Profit</th>
              <th>Logged At</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr><td colSpan={5} className={styles.empty}>No records yet. Click &quot;Log This Week&apos;s Profit&quot; to add your first entry.</td></tr>
            ) : records.map((r, i) => (
              <tr key={r.id} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                <td className={styles.weekCell}>
                  <i className="ti ti-calendar" />
                  {r.weekLabel || formatWeekLabel(r.weekStart)}
                </td>
                <td className={styles.amount}>{php(r.weeklyRevenue)}</td>
                <td className={styles.amount}>{php(r.supplyExpense)}</td>
                <td className={`${styles.amount} ${(r.netProfit || 0) >= 0 ? styles.positive : styles.negative}`}>
                  {php(r.netProfit)}
                </td>
                <td className={styles.muted}>{formatTs(r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Log Weekly Profit</h3>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Select Any Date Within the Week</label>
              <input
                type="date"
                className={styles.modalInput}
                value={weekDate}
                onChange={e => setWeekDate(e.target.value)}
                max={today}
              />
              <span className={styles.weekPreview}>
                <i className="ti ti-calendar-week" /> {formatWeekLabel(getMondayOfWeek(weekDate))}
              </span>
            </div>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Weekly Revenue (₱)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`${styles.modalInput} ${errors.revenue ? styles.inputErr : ''}`}
                value={revenue}
                onChange={e => { setRevenue(e.target.value); setErrors(prev => ({ ...prev, revenue: undefined })); }}
                placeholder="Enter total sales revenue for this week"
              />
              {errors.revenue && <span className={styles.fieldErr}>{errors.revenue}</span>}
            </div>

            <div className={styles.calcBox}>
              <div className={styles.calcRow}>
                <span className={styles.calcLabel}>Supply Order Expense (this week)</span>
                <span className={styles.calcValue}>{php(supplyExpense)}</span>
              </div>
              <div className={styles.calcRow}>
                <span className={styles.calcLabel}>Weekly Revenue</span>
                <span className={styles.calcValue}>{php(revenueNum)}</span>
              </div>
              <div className={`${styles.calcRow} ${styles.calcTotal}`}>
                <span className={styles.calcLabel}>Net Profit</span>
                <span className={`${styles.calcValue} ${netProfit >= 0 ? styles.positive : styles.negative}`}>
                  {php(netProfit)}
                </span>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closeModal} disabled={submitting}>Cancel</button>
              <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
                <i className="ti ti-device-floppy" /> {submitting ? 'Saving…' : 'Save Record'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <ToastNotification message={toast.message} type={toast.type} onDismiss={dismissToast} />}
    </div>
  );
}
